<template>
  <div v-if="visible" class="routine-modal-backdrop" @click.self="close">
    <div class="routine-modal">
      <div class="routine-modal__head">
        <span class="routine-modal__title">{{ form.id ? 'Edit routine' : 'New routine' }}</span>
        <label class="routine-active">
          <span>Active</span>
          <fts-toggle-switch v-model="form.enabled" :size="20" />
        </label>
        <font-awesome-icon :icon="['fas','xmark']" class="routine-close" @click="close" />
      </div>

      <div class="routine-modal__body">
        <section class="routine-section">
          <label class="routine-section-label">Basics</label>
          <input class="fts-form-control" v-model="form.name" placeholder="Routine name" />
          <input class="fts-form-control" v-model="form.description" placeholder="Description (optional)" style="margin-top:8px" />
        </section>

        <section class="routine-section">
          <label class="routine-section-label">When</label>
          <div class="routine-grid-3">
            <select class="fts-form-control" v-model="form.recurringType">
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
              <option value="hour">Every N hours</option>
            </select>
            <input v-if="form.recurringType !== 'hour'" class="fts-form-control" v-model="form.timeOfDay" placeholder="08:00" />
            <input v-else class="fts-form-control" type="number" min="1" v-model.number="form.every" />
            <input class="fts-form-control" v-model="form.timeZone" placeholder="Asia/Kuala_Lumpur" />
          </div>
          <div v-if="form.recurringType === 'week'" class="routine-chip-row">
            <span v-for="d in weekdays" :key="d.value" class="routine-chip" :class="{ 'is-on': form.recurringWeeks.indexOf(d.value) >= 0 }" @click="toggleWeekday(d.value)">{{ d.label }}</span>
          </div>
          <div class="routine-preview-line"><font-awesome-icon :icon="['far','clock']" /> {{ schedulePreview }}</div>
        </section>

        <section class="routine-section">
          <label class="routine-section-label">What it does</label>
          <div class="routine-actions">
            <div class="routine-action is-selected">
              <font-awesome-icon :icon="['far','bell']" />
              <div><div class="ra-title">Send a reminder</div><div class="ra-sub">Notify or digest</div></div>
            </div>
            <div v-for="a in lockedActions" :key="a.t" class="routine-action is-locked">
              <font-awesome-icon :icon="a.icon" />
              <div><div class="ra-title">{{ a.t }} <span class="routine-soon">Soon</span></div><div class="ra-sub">{{ a.s }}</div></div>
            </div>
          </div>
        </section>

        <section class="routine-content-block">
          <div class="routine-content-toggle">
            <span class="rc-on">Write it myself</span>
            <span class="rc-off">Generate with AI <font-awesome-icon :icon="['fas','lock']" /></span>
          </div>
          <textarea class="fts-form-control routine-message" v-model="form.message" :placeholder="ph.message"></textarea>
          <div class="routine-chip-row">
            <span v-for="t in tokens" :key="t" class="routine-token" @click="insertToken(t)">+ {{ t }}</span>
          </div>
          <div class="routine-grid-2">
            <div>
              <label class="routine-sub-label">Deliver via</label>
              <div class="routine-chip-row">
                <span v-for="c in allChannels" :key="c" class="routine-chip" :class="{ 'is-on': form.channels.indexOf(c) >= 0 }" @click="toggleChannel(c)">{{ c }}</span>
              </div>
            </div>
            <div>
              <label class="routine-sub-label">Send to (usernames)</label>
              <div class="routine-recipients">
                <span v-for="(u, i) in form.recipients.ids" :key="u" class="routine-recipient">{{ u }} <font-awesome-icon :icon="['fas','xmark']" @click="removeRecipient(i)" /></span>
                <input class="fts-form-control routine-recipient-input" v-model="recipientInput" @keyup.enter="addRecipient" placeholder="username, Enter" />
              </div>
            </div>
          </div>
        </section>

        <section class="routine-section">
          <div class="routine-targets-head">
            <label class="routine-section-label">Which entries · across modules</label>
            <label class="routine-inline-check"><input type="checkbox" v-model="isDigest" /> Digest</label>
          </div>
          <div v-for="(t, i) in form.targets" :key="i" class="routine-target-row">
            <select class="fts-form-control" v-model="t.vectorId">
              <option :value="0" disabled>Module…</option>
              <option v-for="m in modules" :key="m.id" :value="m.id">{{ m.name }}</option>
            </select>
            <input class="fts-form-control" v-model="t.filter" :placeholder="ph.filter" />
            <font-awesome-icon :icon="['fas','xmark']" class="routine-target-remove" @click="removeTarget(i)" />
          </div>
          <fts-btn text sm @click="addTarget"><font-awesome-icon :icon="['fas','plus']" /> Add module</fts-btn>
          <label class="routine-inline-check" style="margin-top:10px"><input type="checkbox" v-model="form.skipIfEmpty" /> Skip if nothing matches</label>
        </section>

        <section v-if="previewResult" class="routine-preview-result">
          <div class="rpr-head"><font-awesome-icon :icon="['far','eye']" /> Preview — nothing was sent</div>
          <div class="rpr-body">
            <span>Matched <strong>{{ previewResult.matchedCount || 0 }}</strong></span>
            <span v-if="previewResult.summary"> · {{ previewResult.summary }}</span>
            <span class="rpr-status"> · {{ previewResult.status }}</span>
          </div>
        </section>
      </div>

      <div class="routine-modal__foot">
        <fts-btn text :loading="previewing" @click="preview"><font-awesome-icon :icon="['far','eye']" /> Preview (run now)</fts-btn>
        <div class="routine-foot-right">
          <fts-btn text @click="close">Cancel</fts-btn>
          <fts-btn raised color="primary" :loading="saving" :disabled="!form.name" @click="save">Save routine</fts-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { routineStore } from "../store/routineStore";
import { formatSchedule } from "../commons/formatSchedule";
import FTSBtn from "../elements/FTSBtn";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";

export default {
  name: "FTSRoutineDesignModal",
  components: { "fts-btn": FTSBtn, "fts-toggle-switch": FTSToggleSwitch },
  props: {
    visible: { type: Boolean, default: false },
    value: { type: Object, default: null },
    modules: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: this.blank(),
      recipientInput: "",
      previewResult: null,
      saving: false,
      previewing: false,
      weekdays: [
        { value: "monday", label: "Mon" }, { value: "tuesday", label: "Tue" },
        { value: "wednesday", label: "Wed" }, { value: "thursday", label: "Thu" },
        { value: "friday", label: "Fri" }, { value: "saturday", label: "Sat" },
        { value: "sunday", label: "Sun" },
      ],
      allChannels: ["FETIAS", "Email", "Slack", "SMS", "Whatsapp"],
      tokens: ["{{total}}", "{{breakdown}}", "{{count}}"],
      lockedActions: [
        { t: "Create a task", s: "A to-do someone does", icon: ["far", "square-check"] },
        { t: "Run automation", s: "Blocks / webhook", icon: ["fas", "gear"] },
        { t: "Let AI do it", s: "Teammate acts", icon: ["fas", "robot"] },
      ],
      ph: {
        message: "{{total}} items: {{breakdown}}",
        filter: '{"state":"Pending Approval"}',
      },
    };
  },
  computed: {
    schedulePreview() { return formatSchedule(this.form); },
    isDigest: {
      get() { return this.form.notifyMode !== "PerEntry"; },
      set(v) { this.form.notifyMode = v ? "Digest" : "PerEntry"; },
    },
  },
  watch: {
    visible(v) { if (v) this.reset(); },
  },
  methods: {
    blank() {
      return {
        id: 0, name: "", description: "", enabled: true, scope: "Personal",
        recurringType: "day", every: 1, recurringWeeks: [], timeOfDay: "08:00", timeZone: "",
        actionType: "Notify", actor: "Human", notifyMode: "Digest", skipIfEmpty: true,
        contentMode: "Template", message: "{{total}} items: {{breakdown}}", instruction: "",
        channels: ["FETIAS"], recipients: { kind: "Fixed", ids: [] }, targets: [],
      };
    },
    reset() {
      this.previewResult = null;
      this.recipientInput = "";
      if (this.value) {
        const v = JSON.parse(JSON.stringify(this.value));
        v.recurringWeeks = v.recurringWeeks || [];
        v.channels = v.channels || [];
        v.recipients = v.recipients || { kind: "Fixed", ids: [] };
        v.recipients.ids = v.recipients.ids || [];
        v.targets = v.targets || [];
        this.form = Object.assign(this.blank(), v);
      } else {
        this.form = this.blank();
      }
    },
    toggleWeekday(d) {
      const i = this.form.recurringWeeks.indexOf(d);
      if (i >= 0) this.form.recurringWeeks.splice(i, 1); else this.form.recurringWeeks.push(d);
    },
    toggleChannel(c) {
      const i = this.form.channels.indexOf(c);
      if (i >= 0) this.form.channels.splice(i, 1); else this.form.channels.push(c);
    },
    insertToken(t) { this.form.message = ((this.form.message || "") + " " + t).trim(); },
    addRecipient() {
      const u = (this.recipientInput || "").trim();
      if (u && this.form.recipients.ids.indexOf(u) < 0) this.form.recipients.ids.push(u);
      this.recipientInput = "";
    },
    removeRecipient(i) { this.form.recipients.ids.splice(i, 1); },
    addTarget() { this.form.targets.push({ vectorId: 0, scope: "ByFilter", filter: '{"state":"Open"}' }); },
    removeTarget(i) { this.form.targets.splice(i, 1); },
    close() { this.$emit("close"); },
    errText(e) {
      const d = e && e.response && e.response.data;
      return (d && (d.message || JSON.stringify(d))) || (e && e.message) || "Unknown error";
    },
    async save() {
      this.saving = true;
      try {
        const saved = await routineStore.dispatch("saveRoutine", this.form);
        this.$emit("saved", saved);
        this.close();
      } catch (e) {
        alert("Save failed: " + this.errText(e));
      } finally {
        this.saving = false;
      }
    },
    async preview() {
      this.previewing = true;
      this.previewResult = null;
      try {
        let id = this.form.id;
        if (!id) {
          const saved = await routineStore.dispatch("saveRoutine", this.form);
          this.form.id = saved.id;
          id = saved.id;
          this.$emit("saved", saved);
        }
        this.previewResult = await routineStore.dispatch("runRoutine", { id: id, dryRun: true });
      } catch (e) {
        alert("Preview failed: " + this.errText(e));
      } finally {
        this.previewing = false;
      }
    },
  },
};
</script>

<style scoped>
.routine-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 1000; overflow-y: auto; padding: 3vh 1rem;
}
.routine-modal {
  background: #fff; border-radius: 12px; width: 100%; max-width: 640px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2); display: flex; flex-direction: column;
}
.routine-modal__head { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.25rem; border-bottom: 1px solid #eee; }
.routine-modal__title { font-size: 15px; font-weight: 500; flex: 1; }
.routine-active { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; color: #666; margin-right: 14px; }
.routine-close { color: #999; cursor: pointer; }
.routine-modal__body { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 1.1rem; max-height: 72vh; overflow-y: auto; }
.routine-section-label { display: block; font-size: 11px; letter-spacing: .04em; color: #999; margin-bottom: 8px; }
.routine-sub-label { display: block; font-size: 11px; color: #666; margin-bottom: 6px; }
.routine-section { border-top: 1px solid #f2f2f2; padding-top: 0.9rem; }
.routine-section:first-child { border-top: none; padding-top: 0; }
.fts-form-control { width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 13px; box-sizing: border-box; }
.routine-grid-3 { display: grid; grid-template-columns: 1fr 0.8fr 1.3fr; gap: 8px; }
.routine-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 4px; }
.routine-chip-row { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
.routine-chip { font-size: 12px; padding: 4px 10px; border-radius: 999px; border: 1px solid #ddd; color: #888; cursor: pointer; }
.routine-chip.is-on { background: #e6f1fb; color: #185fa5; border-color: #b5d4f4; }
.routine-token { font-size: 11px; padding: 3px 8px; border-radius: 6px; border: 1px solid #ddd; color: #666; cursor: pointer; background: #fafafa; }
.routine-preview-line { display: flex; align-items: center; gap: 6px; margin-top: 8px; font-size: 13px; color: #185fa5; }
.routine-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.routine-action { border: 1px solid #eee; border-radius: 8px; padding: 10px 12px; display: flex; gap: 8px; align-items: flex-start; }
.routine-action.is-selected { border: 2px solid #378add; }
.routine-action.is-locked { opacity: 0.55; }
.ra-title { font-size: 13px; font-weight: 500; }
.ra-sub { font-size: 11px; color: #999; }
.routine-soon { font-size: 10px; background: #f2f2f2; color: #999; padding: 1px 6px; border-radius: 999px; }
.routine-content-block { background: #fafafa; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 12px; }
.routine-content-toggle { display: flex; gap: 8px; }
.rc-on { font-size: 12px; padding: 5px 10px; border-radius: 999px; background: #e6f1fb; color: #185fa5; }
.rc-off { font-size: 12px; padding: 5px 10px; border-radius: 999px; border: 1px solid #ddd; color: #999; display: inline-flex; gap: 5px; align-items: center; }
.routine-message { min-height: 54px; resize: vertical; font-family: inherit; }
.routine-recipients { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.routine-recipient { font-size: 12px; background: #fff; border: 1px solid #ddd; border-radius: 999px; padding: 3px 8px; display: inline-flex; gap: 6px; align-items: center; }
.routine-recipient-input { width: 130px; }
.routine-targets-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.routine-inline-check { font-size: 12px; color: #666; display: inline-flex; gap: 6px; align-items: center; }
.routine-target-row { display: grid; grid-template-columns: 1fr 1.4fr auto; gap: 8px; align-items: center; margin-bottom: 6px; }
.routine-target-remove { color: #999; cursor: pointer; }
.routine-preview-result { background: #e6f1fb; border-radius: 8px; padding: 10px 12px; }
.rpr-head { font-size: 13px; font-weight: 500; color: #185fa5; display: flex; gap: 7px; align-items: center; margin-bottom: 4px; }
.rpr-body { font-size: 12px; color: #444; }
.routine-modal__foot { display: flex; align-items: center; justify-content: space-between; padding: 0.85rem 1.25rem; border-top: 1px solid #eee; }
.routine-foot-right { display: flex; gap: 8px; }
</style>
